import { createFhirDateTime } from '@quromedical/fhir-common';
import type {
  MeasurementName,
  ObservationVitalMeasurement,
} from '@quromedical/models';
import { compact } from '@quromedical/utils';
import { CrudForm, type SubmissionHandler } from 'components/form';
import type { Field } from 'components/types';
import { titles, units } from 'core/vitals';
import { logger } from 'helpers/logger';
import { useCallback, useMemo } from 'react';
import { strings } from 'strings';
import type { ObjectType } from 'validation';
import * as yup from 'yup';

import type { VitalObservationForm } from './types';

type FormValue = Record<MeasurementName, number>;

export interface FormData extends Partial<FormValue> {
  /**
   * Not editable on the form, preset on the form, display only
   */
  unit?: string;
  /**
   * The date/time in the format as provided by masked datetime input
   */
  time: string;
}

/**
 * Generate schema such that the provided measurements are required
 */
const createSchema = (measures: MeasurementName[]) => {
  const dynamicParts = measures.reduce<
    Partial<Record<MeasurementName, yup.AnySchema>>
  >(
    (prev, curr) => ({
      ...prev,
      [curr]: yup.number().positive().required(),
    }),
    {},
  );

  return yup.object<ObjectType<FormData>>({
    ...dynamicParts,
    time: yup.date().required(),
  });
};

const createFields = (measureOptions: MeasurementName[]): Field<FormData>[] => {
  return [
    ...measureOptions.map<Field<FormData>>((measurement) => ({
      subfields: [
        {
          key: measurement,
          type: 'number-box',
          keyboardType: 'decimal-pad',
          label: `${titles[measurement] || measurement}`,
        },
        {
          key: 'unit',
          type: 'text-display',
          label: strings.VitalObservationFormLabelUnit,
          text: units[measurement],
          backgroundColor: 'base-grey',
        },
      ],
    })),
    {
      subfields: [
        {
          key: 'time',
          type: 'date-component',
          maskType: 'datetime',
          label: strings.VitalObservationFormLabelDateTime,
        },
      ],
    },
  ];
};

const getMeasuresFromForm = (
  measurementNames: MeasurementName[],
  formData: FormData,
): ObservationVitalMeasurement[] =>
  compact(
    measurementNames.map<ObservationVitalMeasurement | undefined>((measure) => {
      const foundValue = formData[measure];
      if (!foundValue) {
        return undefined;
      }

      return {
        measurementName: measure,
        ts: new Date(formData.time).getTime(),
        value: foundValue,
      };
    }),
  );

export const VitalObservationMultipleCreateForm: VitalObservationForm = ({
  measurementNames,
  onSubmit,
  onCancel,
}) => {
  const initialValues: Partial<FormData> = {
    time: createFhirDateTime(new Date()),
  };

  const handleSubmit = useCallback<SubmissionHandler<FormData>>(
    async (data: FormData) => {
      try {
        const results = getMeasuresFromForm(measurementNames, data);
        await onSubmit(results);
        return {};
      } catch (error) {
        logger.error(error);
        return { error };
      }
    },
    [measurementNames, onSubmit],
  );

  const schema = useMemo(
    () => createSchema(measurementNames),
    [measurementNames],
  );
  const fields = useMemo(
    () => createFields(measurementNames),
    [measurementNames],
  );

  return (
    <CrudForm
      isNotResponsive
      hasCard={false}
      fields={fields}
      onSubmit={handleSubmit}
      buttonText={strings.ButtonTextSubmit}
      onSecondarySubmit={onCancel}
      secondaryButtonText={strings.ButtonTextCancel}
      showSecondarySubmitButton
      validationSchema={schema}
      initialValues={initialValues}
      onChangeDebounceTime={0}
    />
  );
};
